import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  calculateAndSaveMedical,
  getPlanSpecificMedicalCovers,
  getProposalMedicalDetails,
  saveProposalDraftMedical,
} from './queries';
import { validateProposalPageForm } from './validation';
import {
  convertProposalPageStateToSubmission,
  IProposalDetailsMedicalPageSubmissionModel,
} from './submission';
import { toast } from 'react-toastify';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useParams } from 'react-router-dom';
import CloseProposalDrawer from '../../close-proposal-drawer/CloseProposalDrawer';
import StaticLayout from '@components/layout/StaticLayout';
import Loader from '@components/loader/Loader';
import { ComissionProposalWidget } from '../../proposal-details-page/custom-widgets/commission/ComissionProposalWidget';
import { getProposalStatus, LookupToList } from './utils';
import PolicyProposalQuoteWidget from '../../proposal-details-page/custom-widgets/PolicyProposalQuoteWidget';
import PolicyCostChargesWidget from '../../proposal-details-page/custom-widgets/PolicyCostChargesWidget';
import {
  getProposalPageFormState,
  IProposalCover,
  IProposalPageFormState,
} from './form';
import {
  convertToDetailsSummary,
  convertToPolicyPersons,
  convertToPolicyPlanProposalCovers,
  getPlanCoversLov,
  IProposalDetailsPageResponse,
  IProposalDetailsSummary,
} from './index2';
import PolicyDetailsEntityInfo from '../../proposal-details-page/custom-widgets/PolicyDetailsEntityInfo';
import { PolicyDetailsWidget } from '../../proposal-details-page/custom-widgets/PolicyDetailsWidget';
import PolicyPayerWidget from '../../proposal-details-page/custom-widgets/PolicyPayerWidget';
import PolicyOwnerWidget from '../../proposal-details-page/custom-widgets/PolicyOwnerWidget';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import ProposalMedicalDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalMedicalDetailsWidget';
import ProposalMedicalPrincipalWidget from './custom-widgets/insured-details/ProposalMedicalPrincipalWidget';
import { MedicalInsuredTableWidget } from './custom-widgets/insured-table-widget/MedicalInsuredTableWidget';
import PolicyBeneficiaryWidget from '../../proposal-details-page/custom-widgets/PolicyBeneficiaryWidget';
import PreviewProposalClauseModalShell from '../modals/PreviewProposalClauseModalShell';
import ProposalMedicalGroupDetailsWidget from '../../proposal-details-page/custom-widgets/ProposalMedicalGroupDetailsWidget';
import { IEnhancedMenuItem } from '@components/table/types';
import SimpleActionBar from '@components/menu/SimpleActionBar';
import { useStepperStyles, useTabsLayoutStyles } from '@styles/sharedStyles';

const ProposalMedicalPage: React.FC = () => {
  const { classes: tabClasses } = useTabsLayoutStyles();
  const { classes: stepperClasses } = useStepperStyles({
    mainContainerRatio: 8,
  });
  const [issueModalOpen, setIssueModalOpen] = useState<boolean>(false);
  const [closeProposalDrawerOpen, setCloseProposalDrawerOpen] =
    useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const params = useParams();
  const entityId = params.id;

  const [pageState, onPageStateUpdate] = useState<IProposalPageFormState>(
    getProposalPageFormState()
  );

  const [data, setData] = useState<IProposalDetailsSummary>();

  const ownerIsDifferent: boolean =
    pageState?.values?.policyPersons?.payer?.ownerIsDifferent || false;

  const [coversList, setCoversList] = useState<IProposalCover[]>([]);

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    classes: {},
    levels: {},
    policyCovers: {},
    cnssOptions: {},
    insuredRelations: {},
    continuity: {},
    gender: {
      MALE: 'Male',
      FEMALE: 'Female',
    },
  });

  const [saveAndCalculationAction] = useMutation(calculateAndSaveMedical());

  const [saveAsDraftAction] = useMutation(saveProposalDraftMedical());

  useEffect(() => {
    initialize();
  }, []);

  const isClosed =
    data?.Status === 'CLOSED_WON' || data?.Status === 'CLOSED_LOST';

  const disabledPage = isClosed || submitting || loading;

  const [getProposalDetailsLazy] = useLazyQuery(getProposalMedicalDetails());

  const [getNonSelectedPlanCoverLazy] = useLazyQuery(
    getPlanSpecificMedicalCovers()
  );

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    setLoading(true);
    const result: { data: IProposalDetailsPageResponse } =
      await getProposalDetailsLazy({
        variables: {
          id: entityId,
          isPrincipal: null,
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

    const summaryData = convertToDetailsSummary(result.data);

    const initialCovers = convertToPolicyPlanProposalCovers(result.data);
    const initialPersons = convertToPolicyPersons(result.data);

    const planCoverDetails = await getPlanCoverDetails(
      summaryData.PlanID.Id,
      []
    );

    const lists = LookupToList(result.data, summaryData.LineId.ExternalCode);
    const lovCovers = getPlanCoversLov(planCoverDetails);
    setLovs({
      classes: lists.classes,
      levels: lists.levels,
      policyCovers: lovCovers,
      cnssOptions: lists.cnssOptions,
      continuity: lists.continuity,
      insuredRelations: lists.insuredRelations,
      gender: lovs.gender,
    });

    setCoversList(initialCovers);
    onPageStateUpdate(getProposalPageFormState(summaryData, initialPersons));
    setData(summaryData);
    setLoading(false);
  };

  const renderLeftSection = () => {
    if (data) {
      return <PolicyDetailsEntityInfo data={data as any} />;
    }

    return <></>;
  };

  const onSubmit = async () => {
    const validationResult = validateProposalPageForm(data, pageState.values);

    if (!submitting) {
      setSubmitting(true);
      const newPageState = {
        ...pageState,
        errors: validationResult.errors,
        touched: validationResult.touched,
      };

      onPageStateUpdate(newPageState);
      if (validationResult.isValid) {
        const dataToSubmit = convertProposalPageStateToSubmission(
          data,
          pageState
        );

        try {
          const result = await saveAndCalculationAction({
            variables: { ...dataToSubmit },
            errorPolicy: 'all',
          });
          if (isEmpty(result.errors)) {
            // setDeactivateButtonState("success");
            toast.success(
              <ToastSuccessMessage>
                {'Proposal successfully saved.'}
              </ToastSuccessMessage>
            );
            initialize();
          } else {
            // setRegenerateButtonState(undefined);
            toast.error(
              <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
            );
          }
        } catch (err) {
          toast.error(
            <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
          );
          setSubmitting(false);
        }
      } else {
        toast.error(<ToastErrorMessage>Incomplete Form</ToastErrorMessage>);
      }
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = convertProposalPageStateToSubmission(
        data,
        pageState
      ) as Partial<IProposalDetailsMedicalPageSubmissionModel>;

      if (isEmpty(dataToSubmit.policyPersonInput[0]?.personId)) {
        dataToSubmit.policyPersonInput = [];
      }

      try {
        const result = await saveAsDraftAction({
          variables: { ...dataToSubmit },
          errorPolicy: 'all',
        });

        if (isEmpty(result.errors)) {
          // setDeactivateButtonState("success");
          toast.success(
            <ToastSuccessMessage>
              {'Proposal successfully saved.'}
            </ToastSuccessMessage>
          );

          initialize();
        } else {
          // setRegenerateButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      } catch (err) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        setSubmitting(false);
      }
    }
    setSubmitting(false);
  };

  const onIssuePolicyAction = async () => {
    setIssueModalOpen(true);
  };

  const renderActions = () => {
    const actions: IEnhancedMenuItem[] = [
      {
        name: 'saveDraft',
        title: 'Save Draft',
        onClick: () => {
          onSaveDraft();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed || data?.Status?.toLowerCase() !== 'new',
      },
      {
        name: 'calculateAndSave',
        title: 'Calculate & Save',
        onClick: () => {
          onSubmit();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed || !isValidNumber(data?.LineId?.ExternalCode),
      },
      {
        name: 'issuePolicy',
        title: 'Issue Policy',
        onClick: () => {
          onIssuePolicyAction();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            isValidNumber(data?.LineId?.ExternalCode)
          ),
      },
      {
        name: 'closeProposal',
        title: 'Close Proposal',
        onClick: () => {
          setCloseProposalDrawerOpen(true);
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden: isClosed,
      },
    ];

    return <SimpleActionBar items={actions} />;
  };

  const renderMainChildren = () => {
    if (data) {
      // const isClosed = status === "closedwon" || "closedlost";
      const isProposalWon = data.Status?.toUpperCase() === 'CLOSED_WON';
      const isProposalUserNew = data.Status?.toLowerCase() === 'new';
      const steps = isProposalUserNew
        ? ['New', 'In Progress', 'Closed Won']
        : ['New', 'In Progress', isProposalWon ? 'Closed Won' : 'Closed Lost'];

      return (
        <section>
          <div className={stepperClasses.stepperContainer}>
            <div className={stepperClasses.stepperWrapper}>
              <EnhancedStepper
                activeStep={getProposalStatus(steps, data.Status)}
                steps={steps}
                buttonTitle="Calculate & Save"
                buttonOnClick={() => undefined}
              />
            </div>
            <div className={stepperClasses.actionsWrapper}>
              {renderActions()}
            </div>
          </div>
          <div className={tabClasses.tabSplitBody}>
            <div className={tabClasses.tabMainBody}>
              <PolicyDetailsWidget data={data as any} />
              <PolicyCostChargesWidget
                pageState={pageState as any}
                disabledForm={disabledPage}
                status={data.Status}
                line={data?.LineId.ExternalCode?.toString() || ''}
                editableLine={data.BusinessUserID?.EditableLineChargesIDs?.includes(
                  data?.LineId.Id
                )}
                onPageStateUpdate={onPageStateUpdate as any}
                currencySymbol={data.PolicyCurrency.Symbol}
              />
              <WidgetPaper>
                <WidgetSection title="Payer & Owner">
                  <PolicyPayerWidget
                    pageState={pageState as any}
                    onPageStateUpdate={onPageStateUpdate as any}
                    disabledForm={disabledPage}
                    data={data as any}
                  />
                  {ownerIsDifferent && (
                    <PolicyOwnerWidget
                      pageState={pageState as any}
                      onPageStateUpdate={onPageStateUpdate as any}
                      disabledForm={disabledPage}
                      data={data as any}
                    />
                  )}
                </WidgetSection>
              </WidgetPaper>

              <PolicyBeneficiaryWidget
                pageState={pageState as any}
                onPageStateUpdate={onPageStateUpdate as any}
                lovs={lovs}
                disabledForm={disabledPage}
                maxNumber={data.PlanID.MaxNbBeneficiary}
                data={data as any}
              />

              {data?.LineId?.ExternalCode?.toString() === '19' && (
                <>
                  <ProposalMedicalDetailsWidget
                    pageState={pageState as any}
                    onPageStateUpdate={onPageStateUpdate as any}
                    lovs={lovs}
                    data={data as any}
                    disabledForm={disabledPage}
                  />

                  <ProposalMedicalPrincipalWidget
                    pageState={pageState as any}
                    onPageStateUpdate={onPageStateUpdate as any}
                    lovs={lovs}
                    data={data as any}
                    maxNumber={data.PlanID.MaxNbInsured}
                    disabledForm={disabledPage}
                  />

                  {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                    <MedicalInsuredTableWidget data={data} />
                  )}
                </>
              )}

              {data?.LineId?.ExternalCode?.toString() === '6' && (
                <>
                  <ProposalMedicalGroupDetailsWidget
                    pageState={pageState as any}
                    status={data.Status}
                    allowPlanCoverEdits={
                      data.BusinessUserID.AllowPlanCoverEdits
                    }
                    onPageStateUpdate={(pageState) => {
                      onPageStateUpdate(pageState);
                    }}
                    lovs={lovs}
                    data={data as any}
                    disabledForm={disabledPage}
                  />

                  {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                    <MedicalInsuredTableWidget data={data} />
                  )}
                </>
              )}

              {closeProposalDrawerOpen && (
                <CloseProposalDrawer
                  open={closeProposalDrawerOpen}
                  onClose={() => {
                    setCloseProposalDrawerOpen(false);
                  }}
                  onSuccess={() => {
                    initialize();
                    setCloseProposalDrawerOpen(false);
                  }}
                  id={entityId}
                />
              )}
            </div>
            <div className={tabClasses.tabRightSection}>
              <PolicyProposalQuoteWidget
                data={data as any}
                covers={coversList as any}
              />

              {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                <ComissionProposalWidget
                  data={data as any}
                  onUpdateSuccess={() => {
                    initialize();
                  }}
                />
              )}
            </div>
          </div>
        </section>
      );
    }

    return <></>;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <StaticLayout
        name={'Proposal Details'}
        leftChildren={renderLeftSection()}
        mainChildren={renderMainChildren()}
      />
      {issueModalOpen && (
        <PreviewProposalClauseModalShell
          data={data}
          onSuccess={() => {
            setIssueModalOpen(false);
            initialize();
          }}
          onClose={() => setIssueModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProposalMedicalPage;
